import React from 'react';
import {
  Accordion,
  Switch,
  TextArea,
  TextField,
  SelectMenu,
  Typography,
  TreeList,
} from '@scale/scaleui';
import { MultiTurnInteractionConfiguration, MessageRole } from '@scale/shared/types/chat';
import { ChatDocumentationLink } from '@scale/shared/chat/documentation';
import { Alert } from '../../TempComponents';
import ConfigSectionHeader from '../components/ConfigSectionHeader';
import _ from 'lodash';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import { PROMPT_GENERATION } from 'react-scale/components/scalers/tasks/ThreadedInteraction/constants';
import { DeprecatedFeature, ExperimentalFeature } from '../components/ConfigLifeCycleInfo';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface PromptOptionsProps {
  value: MultiTurnInteractionConfiguration;
  onSubmit: (config: MultiTurnInteractionConfiguration) => void;
  showEnableAttachmentsSwitch: boolean;
  useAccordion?: boolean;
  projectId?: string;
}

// eslint-disable-next-line complexity
const PromptOptions = ({
  value,
  onSubmit,
  showEnableAttachmentsSwitch,
  useAccordion = true,
  projectId,
}: PromptOptionsProps) => {
  const WrappingComponent = useAccordion ? Accordion : ConfigSectionHeader;
  const isPromptGenerationFeatureEnabled = useUnleashFeatureFlag(
    'chat-show-prompt-suggestions',
    { projectId },
    false,
  );
  return (
    <div className="flex flex-col gap-2">
      <Typography.H3>Prompt Options</Typography.H3>
      <Typography.Paragraph2 color="text-neutral-500">
        Configure prompt options such as reference texts, attachments, and system prompts.
      </Typography.Paragraph2>
      <WrappingComponent header={'General Prompt Configs'}>
        <div className="flex flex-col gap-2">
          <div className="space-y-2">
            <TextArea
              id="end-session-jsonata"
              label="[Multiturn] JSONata Condition for Ending the Session"
              value={value.endSessionCondition}
              onChange={e => onSubmit({ ...value, endSessionCondition: e.target.value })}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Add a JSONata expression to determine when the chat session should end. This can be
              used to end the conversation early based on certain step responses. Use
              metadata.responses to refer to the responses array. <br /> <br />
              Note: JSONata expressions override minimum turn validation. If you want to enforce a
              minimum number of turns, it must be included in the JSONata condition.
            </Typography.Paragraph2>
          </div>

          <div className="space-y-2">
            <Switch
              altDescription="[Multiturn] Use Default User Message"
              label="[Multiturn] Use Default User Message"
              checked={value.useDefaultUserMessage}
              onCheckedChange={checked => onSubmit({ ...value, useDefaultUserMessage: checked })}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Automatically populate a default message for user turns after the first turn, so that
              these turns do not require prompt inputs.
            </Typography.Paragraph2>
            <Typography.Paragraph2 color="text-neutral-500">
              This feature is incompatible with process supervision.
            </Typography.Paragraph2>
            {!!value.useDefaultUserMessage && (
              <TreeList childContainerClassName="w-full max-w-none">
                <Switch
                  altDescription="Use Previous User Message"
                  label="Use Previous User Message"
                  checked={value.usePreviousUserMessage}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, usePreviousUserMessage: checked })
                  }
                />
                <Switch
                  altDescription="Allow Default User Message Edits"
                  label="Allow Default User Message Edits"
                  checked={value.defaultUserMessageEditable}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, defaultUserMessageEditable: checked })
                  }
                />
                <TextArea
                  id="defaultUserMessage"
                  label={'Default User Message'}
                  placeholder="Examples: 'Continue to the next step.'"
                  value={value.defaultUserMessage}
                  onChange={e => onSubmit({ ...value, defaultUserMessage: e.target.value })}
                />
              </TreeList>
            )}
          </div>

          <div className="mb-4">
            <Switch
              altDescription="Use Reference Text"
              label="Use Reference Text"
              checked={value.useReferenceText}
              onCheckedChange={checked =>
                onSubmit({
                  ...value,
                  useReferenceText: checked,
                  isReferenceTextRequired: checked ? value.isReferenceTextRequired : false,
                  minReferenceTexts: checked ? value.minReferenceTexts : undefined,
                })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Allow the user to provide a reference text as part of the prompt.
            </Typography.Paragraph2>
            {!!value.useReferenceText && (
              <TreeList>
                <SelectMenu
                  options={[
                    {
                      id: 'promptOnly',
                      name: 'Enable reference text in the prompt for the first turn only',
                    },
                    {
                      id: 'promptAndMultiTurn',
                      name: 'Enable reference text in the prompt for all turns',
                    },
                  ]}
                  selectedOptionId={
                    value.useReferenceTextOnMultiTurn ? 'promptAndMultiTurn' : 'promptOnly'
                  }
                  setSelected={val =>
                    onSubmit({
                      ...value,
                      useReferenceTextOnMultiTurn: val === 'promptAndMultiTurn',
                      // if reference texts are only enabled on the first turn, also update requireReferenceTextOnlySingleTurn if it's false
                      requireReferenceTextOnlySingleTurn:
                        val === 'promptOnly' &&
                        value.isReferenceTextRequired &&
                        value.requireReferenceTextOnlySingleTurn === false
                          ? true
                          : value.requireReferenceTextOnlySingleTurn,
                    })
                  }
                />
                <div>
                  <Switch
                    altDescription="Make Reference Text Required"
                    label="Make Reference Text Required"
                    checked={value.isReferenceTextRequired}
                    onCheckedChange={checked =>
                      onSubmit({
                        ...value,
                        isReferenceTextRequired: checked,
                        minReferenceTexts: checked ? 1 : 0,
                      })
                    }
                  />
                  {!!value.isReferenceTextRequired && (
                    <TreeList>
                      <SelectMenu
                        options={[
                          ...(value.useReferenceTextOnMultiTurn
                            ? [
                                {
                                  id: 'promptAndMultiTurn',
                                  name: 'Require reference text in the prompt for all turns',
                                },
                              ]
                            : []),
                          {
                            id: 'promptOnly',
                            name: 'Require reference text in the prompt for the first turn only',
                          },
                        ]}
                        placeholder="Select when to require reference text"
                        selectedOptionId={
                          value.requireReferenceTextOnlySingleTurn
                            ? 'promptOnly'
                            : 'promptAndMultiTurn'
                        }
                        setSelected={val =>
                          onSubmit({
                            ...value,
                            requireReferenceTextOnlySingleTurn: val === 'promptOnly',
                          })
                        }
                      />
                      <TextField
                        id="minReferenceTexts"
                        label="Min Number of Required Reference Texts"
                        placeholder="1"
                        helperText={`Applies to ${
                          value.requireReferenceTextOnlySingleTurn
                            ? 'the first turn only'
                            : 'all turns'
                        }`}
                        value={value.minReferenceTexts}
                        onChange={e =>
                          onSubmit({ ...value, minReferenceTexts: parseInt(e.target.value) })
                        }
                      />
                    </TreeList>
                  )}
                </div>
                <Switch
                  altDescription="Make Reference Text Read-Only"
                  label="Make Reference Text Read-Only"
                  checked={value.isReferenceTextReadOnly}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, isReferenceTextReadOnly: checked })
                  }
                />
                <Switch
                  altDescription="Hide Reference URL"
                  label="Hide Reference URL"
                  checked={value.hideReferenceURL}
                  onCheckedChange={checked => onSubmit({ ...value, hideReferenceURL: checked })}
                />
                <Switch
                  altDescription="Show Reference Category"
                  label="Show Reference Category"
                  checked={value.showReferenceCategory}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, showReferenceCategory: checked })
                  }
                />
                <TextField
                  id="referenceTextCategories"
                  label="Seeded Reference Text Category Options"
                  placeholder="Enter a comma-separated list of reference text categories"
                  value={value.referenceTextCategories}
                  onChange={e => onSubmit({ ...value, referenceTextCategories: e.target.value })}
                  disabled={!value.showReferenceCategory}
                />
                <Switch
                  altDescription="Allow Selecting PDF Attachment as Reference Text"
                  label="Allow Selecting PDF Attachment as Reference Text"
                  checked={value.allowSelectPdfAttachmentAsReferenceText}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, allowSelectPdfAttachmentAsReferenceText: checked })
                  }
                />
                <TextField
                  id="maxReferenceTexts"
                  label="Max Number of Reference Texts"
                  placeholder="1"
                  value={value.maxReferenceTexts}
                  onChange={e =>
                    onSubmit({ ...value, maxReferenceTexts: parseInt(e.target.value) })
                  }
                />
                <Switch
                  altDescription="Append Reference Text to Prompt"
                  label="Append Reference Text to Prompt"
                  helperText="When enabled, the reference text will be appended to the prompt, rather than being prepended, and reference text tags will be omitted."
                  checked={value.useAppendedReferenceText}
                  onCheckedChange={checked =>
                    onSubmit({ ...value, useAppendedReferenceText: checked })
                  }
                />
                <TextField
                  id="minReferenceTextsLength"
                  label="Min Number of Reference Text Characters"
                  value={value.minReferenceTextsLength}
                  onChange={e => {
                    const parsed = parseInt(e.target.value);
                    onSubmit({
                      ...value,
                      minReferenceTextsLength: isNaN(parsed) ? undefined : parsed,
                    });
                  }}
                />
                <TextField
                  id="maxReferenceTextsLength"
                  label="Max Number of Reference Text Characters"
                  value={value.maxReferenceTextsLength}
                  onChange={e => {
                    const parsed = parseInt(e.target.value);
                    onSubmit({
                      ...value,
                      maxReferenceTextsLength: isNaN(parsed) ? undefined : parsed,
                    });
                  }}
                />
              </TreeList>
            )}
          </div>

          <div className="mb-4">
            <Switch
              altDescription="Enable Attachments"
              label="Enable Attachments"
              checked={value.useAttachments}
              onCheckedChange={checked =>
                onSubmit({
                  ...value,
                  useAttachments: checked,
                  isAttachmentRequired: checked ? value.isAttachmentRequired : false,
                  minAttachments: checked ? value.minAttachments : undefined,
                })
              }
              disabled={!showEnableAttachmentsSwitch}
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enable attachments to allow contributors to attach files during the chat session.{' '}
              {!showEnableAttachmentsSwitch
                ? 'This feature is disabled because the chat models used in this task do not support attachments.'
                : ''}
            </Typography.Paragraph2>

            {!!value.useAttachments && (
              <TreeList>
                <SelectMenu
                  options={[
                    {
                      id: 'promptOnly',
                      name: 'Enable attachments in the prompt for the first step only',
                    },
                    {
                      id: 'promptAndMultiTurn',
                      name: 'Enable attachments in the prompt for all steps',
                    },
                  ]}
                  // set default behavior to promptAndMultiTurn if useAttachmentsOnMultiturn is not set
                  selectedOptionId={
                    value.useAttachmentsOnMultiTurn || _.isNil(value.useAttachmentsOnMultiTurn)
                      ? 'promptAndMultiTurn'
                      : 'promptOnly'
                  }
                  setSelected={val =>
                    onSubmit({
                      ...value,
                      useAttachmentsOnMultiTurn: val === 'promptAndMultiTurn',
                    })
                  }
                />

                <Switch
                  className="mb-4"
                  altDescription="Make Attachments Required"
                  label="Make Attachments Required"
                  checked={value.isAttachmentRequired}
                  onCheckedChange={checked =>
                    onSubmit({
                      ...value,
                      isAttachmentRequired: checked,
                      minAttachments: checked ? 1 : 0,
                    })
                  }
                />
              </TreeList>
            )}
          </div>
          <ExperimentalFeature
            name="Prompt Suggestions"
            details="This feature is highly experimental and may affect prompt diversity if not used with caution. Please consult prompt engineering team for the recommended usage."
            visibilityCondition={!!isPromptGenerationFeatureEnabled}
            showInfoCondition={
              !!value.isPromptGenerationEnabled || !!value.isPreSeededPromptSuggestionEnabled
            }
          >
            <div className="mb-4">
              <Switch
                checked={value.isPromptGenerationEnabled}
                onCheckedChange={checked =>
                  onSubmit({
                    ...value,
                    isPromptGenerationEnabled: checked,
                  })
                }
                label={'Show Prompt Suggestions'}
              />
              <Typography.Paragraph2 color="text-neutral-500">
                Show prompt suggestions for every prompt input.
              </Typography.Paragraph2>
              {!!value.isPromptGenerationEnabled && (
                <TreeList childContainerClassName="w-full max-w-none">
                  <Switch
                    helperText="Support pre-seeding prompt suggestions from template variables for the first turn. Select this only 
                  if prompt suggestions are already generated and being uploaded as part of the task."
                    altDescription="Use Pre-seeded Prompt Suggestions"
                    label="Use Pre-seeded Prompt Suggestions"
                    checked={value.isPreSeededPromptSuggestionEnabled}
                    onCheckedChange={checked =>
                      onSubmit({ ...value, isPreSeededPromptSuggestionEnabled: checked })
                    }
                  />
                  {value.isPreSeededPromptSuggestionEnabled ? (
                    <StringInputBuilder
                      key="pre-seeded-prompt-suggestion"
                      name={'Pre-seeded Prompt Suggestions'}
                      hint={
                        'Enter the template variable containing the pre-seeded prompt suggestions (ex: {{prompt_suggestions}}). The suggestions should be JSON parsable, like: ["this is the first suggestion", "here\'s the second"]'
                      }
                      existingValue={value.preSeededPromptSuggestions}
                      updateProperty={(preSeededPromptSuggestions: string) =>
                        onSubmit({
                          ...value,
                          preSeededPromptSuggestions,
                        })
                      }
                      required
                    />
                  ) : (
                    <>
                      <StringInputBuilder
                        name={'System Prompt for Prompt Generation'}
                        hint={
                          'The system prompt to use for generating prompts. This will be used to generate prompts for the user. Make sure your system prompt specifies the number of prompts you would like to generate.'
                        }
                        existingValue={value.promptGenerationSystemPrompt}
                        updateProperty={(promptGenerationSystemPrompt: string) =>
                          onSubmit({
                            ...value,
                            promptGenerationSystemPrompt,
                          })
                        }
                        required
                      />
                      <SelectMenu
                        className="w-fit"
                        label="Prompt Generation Model"
                        options={PROMPT_GENERATION.MODELS.map(model => ({
                          name: model.name,
                          id: model.id,
                        }))}
                        selectedOptionId={value.promptGenerationModel || ''}
                        setSelected={(promptGenerationModel: string) => {
                          onSubmit({
                            ...value,
                            promptGenerationModel,
                          });
                        }}
                        required
                      />
                    </>
                  )}
                </TreeList>
              )}
            </div>
          </ExperimentalFeature>

          <div className="space-y-2">
            <Switch
              className="mb-2"
              altDescription="Enable Custom Roles"
              label="Enable Custom Roles"
              checked={value.showMessageRoles}
              onCheckedChange={checked =>
                onSubmit({
                  ...value,
                  showMessageRoles: checked,
                })
              }
            />
            <Typography.Paragraph2 color="text-neutral-500">
              Enable contributors to choose a different role for their prompt input.
            </Typography.Paragraph2>
            {!!value.showMessageRoles && (
              <TreeList childContainerClassName="w-full max-w-none">
                <SelectMenu
                  className="mb-2 w-fit"
                  label="Enabled Message Roles"
                  options={[
                    {
                      id: MessageRole.User as string,
                      name: 'User',
                    },
                    {
                      id: MessageRole.Function as string,
                      name: 'Function',
                    },
                  ]}
                  selectedOptionId={(value.messageRoleOptions as string[]) || []}
                  setSelected={val => {
                    onSubmit({ ...value, messageRoleOptions: val as unknown as MessageRole[] });
                  }}
                  multiple
                />
                <TextArea
                  label="Contributor Instructions for Message Roles"
                  value={value.messageRolePromptInputHint}
                  onChange={e => onSubmit({ ...value, messageRolePromptInputHint: e.target.value })}
                  placeholder="Select the role of the message."
                />
              </TreeList>
            )}
          </div>

          <div className="space-y-2">
            <DeprecatedFeature
              name="Prompt Skipping"
              details="This feature is only intended for Native Tool Use projects, usage in other projects may lead to unexpected behavior. This feature will be deprecated at the end of 2025 Q1."
              showInfoCondition={!!value.enableSkipPromptInput}
            >
              <Switch
                altDescription="Enable Prompt Skipping"
                label="Enable Prompt Skipping"
                checked={value.enableSkipPromptInput}
                onCheckedChange={checked => onSubmit({ ...value, enableSkipPromptInput: checked })}
              />
              <Typography.Paragraph2 color="text-neutral-500">
                [Primarily for Native Tool Use] Allow user turns to be skipped. Select this when the
                model is expected to support responding directly to a previous model response.
              </Typography.Paragraph2>
              {!!value.enableSkipPromptInput && (
                <TreeList childContainerClassName="w-full max-w-none">
                  <Switch
                    key="auto-skip-after-tool-call"
                    label="Auto skip prompt input after tool call"
                    checked={value.autoSkipAfterToolCall}
                    onCheckedChange={checked =>
                      onSubmit({ ...value, autoSkipAfterToolCall: checked })
                    }
                  />
                  <Switch
                    key="auto-skip-after-tool-response"
                    label="Auto skip prompt input after tool response"
                    labelDescription="Caution: This currently only works for Lazarus and Kratos"
                    checked={value.autoSkipAfterToolResponse}
                    onCheckedChange={checked =>
                      onSubmit({ ...value, autoSkipAfterToolResponse: checked })
                    }
                  />
                </TreeList>
              )}
            </DeprecatedFeature>
          </div>

          <div className="space-y-2">
            <TextArea
              id="systemPrompt"
              label={'System Prompt for Chat History'}
              placeholder="Examples: 'You are a middle school science student studying for an exam.' or 'Answer in 2 sentences or less.'"
              value={value.systemPrompt}
              onChange={e => onSubmit({ ...value, systemPrompt: e.target.value })}
            />
            {!!value.systemPrompt?.length && (
              <TreeList>
                {[
                  <Switch
                    key="system-prompt-gpt-only"
                    altDescription="Only Apply System Prompt to GPT Responses"
                    label="Only Apply System Prompt to GPT Responses"
                    checked={value.systemPromptGptOnly}
                    onCheckedChange={checked =>
                      onSubmit({ ...value, systemPromptGptOnly: checked })
                    }
                  />,
                ]}
              </TreeList>
            )}
            <Typography.Paragraph2 color="text-neutral-500">
              See System Prompt documentation{' '}
              <a
                className="text-primary-500"
                target="_blank"
                rel="noopener noreferrer"
                href={ChatDocumentationLink.SystemPrompts}
              >
                here.
              </a>
            </Typography.Paragraph2>
            <TextArea
              id="userPromptTemplate"
              label="User Prompt Template"
              placeholder="Enter a template with {prompt_input} to format every user message. Example: 'User said: {prompt_input}'"
              value={value.userPromptTemplate}
              onChange={e => onSubmit({ ...value, userPromptTemplate: e.target.value })}
            />
            {!!value?.userPromptTemplate?.length && !value?.userPromptTemplate?.includes('}') && (
              <Alert variant="warning">
                The template must include the placeholder {'{prompt_input}'} to display the user
                message, or another variable.
              </Alert>
            )}
          </div>
        </div>
      </WrappingComponent>
    </div>
  );
};

export default PromptOptions;
